import { freshDiscovery } from './discovery-state.js';
import { makeCode } from './codes.js';
// Save system: localStorage (instant) + cloud backup through the Worker (/api/save/:code).
const KEY = 'saicraft.save.v1';
export const RECOVERY_KEY = 'saicraft.recovery.v1';
export { makeCode };

export function defaultSave() {
  return {
    v: 1,
    created: Date.now(),
    updatedAt: 0,
    name: 'Sai',
    cloudCode: makeCode(),
    playerId: null,
    friends: { recent: [] },
    xp: 0,
    coins: 0,
    gems: 0,
    levels: {},
    avatar: { tone: 3, hair: 0, style: 'short' },
    owned: { skin: ['classic'], sword: ['wood'], trail: ['none'], pet: ['none', 'dog'] },
    equip: { skin: 'classic', sword: 'wood', trail: 'none', pet: 'none' },
    unlocks: { doubleJump: false },
    stats: {
      answered: 0, correct: 0, firstTry: 0, bestStreak: 0, mobs: 0, stomps: 0, lucky: 0, coinsTotal: 0, gemsTotal: 0,
      secrets: 0, playTime: 0, levelsDone: 0, jumps: 0, bosses: 0, falls: 0, chests: 0, perfect: 0, noDamage: 0, sessions: 0,
      blitzGames: 0, threeStars: 0, challenges: 0, giants: 0,
    },
    topics: {},
    facts: {},
    ach: {},
    daily: { lastPlay: null, streak: 0, lastClaim: null, day: 0, bestStreak: 0 },
    blitz: { best: 0, scores: [] },
    settings: { music: 0.55, sfx: 0.8, quality: 'auto', autoJump: true, readAloud: false, difficulty: 'auto', cameraDistance: 7.5, topics: {}, tables: [], haptics: true, worldListed: true },
    discovery: freshDiscovery(),
    seenIntro: false,
    history: [],
  };
}

function merge(base, over) {
  if (over === null || over === undefined) return base;
  if (Array.isArray(base)) return Array.isArray(over) ? over : base;
  if (typeof base !== 'object' || base === null) return typeof over === typeof base || base === null ? over : base;
  const out = { ...base };
  for (const k of Object.keys(over)) {
    if (k in base && base[k] !== null && typeof base[k] === 'object' && !Array.isArray(base[k])) out[k] = merge(base[k], over[k]);
    else out[k] = over[k];
  }
  return out;
}

class SaveManager {
  constructor() {
    this.data = null;
    this.cloudTimer = null;
    this.lastPush = 0;
    this.cloudStatus = 'unknown';
  }

  load() {
    let data = null;
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) data = merge(defaultSave(), JSON.parse(raw));
    } catch (e) {
      data = null;
    }
    this.data = data || defaultSave();
    this.fresh = !data;
    this.retryBackups();
    window.addEventListener('pagehide', () => this.persist(true));
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.persist(true);
    });
    return this.data;
  }

  persist(flushCloud = false) {
    if (!this.data) return;
    this.data.updatedAt = Date.now();
    this.store();
    if (flushCloud) this.pushCloud(true);
    else this.scheduleCloud();
  }

  // Writes this device's copy without touching updatedAt: only real progress makes a save newer.
  store() {
    try {
      localStorage.setItem(KEY, JSON.stringify(this.data));
    } catch (e) {
      /* storage full / private mode */
    }
  }

  scheduleCloud() {
    if (this.cloudTimer) return;
    // KV's free tier allows 1,000 writes a day, so routine backups wait a minute; flushes still go at once.
    const wait = Math.max(15000, 60000 - (Date.now() - this.lastPush));
    this.cloudTimer = setTimeout(() => {
      this.cloudTimer = null;
      this.pushCloud();
    }, wait);
  }

  async pushCloud(beacon = false) {
    // A stand-in that hasn't loaded this player's cloud game yet must never replace it.
    if (!this.data || !this.data.cloudCode || this.data.needsPull) return false;
    if (location.protocol === 'file:') return false;
    clearTimeout(this.cloudTimer);
    this.cloudTimer = null;
    this.lastPush = Date.now();
    const status = await this.upload(this.data, beacon);
    this.cloudStatus = !status ? 'offline' : status < 300 ? 'ok' : 'error';
    return this.cloudStatus === 'ok';
  }

  // PUT one save under its own code. Resolves to the HTTP status, or 0 when offline.
  async upload(data, beacon = false) {
    if (!data?.cloudCode || location.protocol === 'file:') return 0;
    const body = JSON.stringify(data);
    try {
      const res = await fetch(`/api/save/${encodeURIComponent(data.cloudCode)}`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body,
        keepalive: beacon && body.length < 60000,
      });
      return res.status;
    } catch (e) {
      return 0;
    }
  }

  // A displaced game stays on this device until its own cloud code has a confirmed backup.
  // Recovery copies are device-only and never become part of another player's save.
  recoveryCopies() {
    try {
      const raw = localStorage.getItem(RECOVERY_KEY);
      if (!raw) return {};
      const copies = JSON.parse(raw);
      return copies && typeof copies === 'object' && !Array.isArray(copies) ? copies : null;
    } catch (e) {
      return null;
    }
  }

  storeRecovery(copies) {
    try {
      if (Object.keys(copies).length) localStorage.setItem(RECOVERY_KEY, JSON.stringify(copies));
      else localStorage.removeItem(RECOVERY_KEY);
      return true;
    } catch (e) {
      return false;
    }
  }

  backup(data) {
    if (!data?.cloudCode || data.needsPull) return false;
    const copies = this.recoveryCopies();
    if (!copies) return false;
    const previous = copies[data.cloudCode];
    const copy = previous && (previous.updatedAt || 0) > (data.updatedAt || 0) ? previous : JSON.parse(JSON.stringify(data));
    copies[data.cloudCode] = copy;
    if (!this.storeRecovery(copies)) return false;
    this.uploadBackup(copy);
    return true;
  }

  retryBackups() {
    for (const copy of Object.values(this.recoveryCopies() || {}))
      if (copy?.v === 1 && copy.cloudCode && !copy.needsPull) this.uploadBackup(copy);
  }

  async uploadBackup(copy) {
    const status = await this.upload(copy);
    // 409 means the code already has a newer cloud copy. Failed uploads stay available for retry.
    if (!(status >= 200 && status < 300) && status !== 409) return;
    const copies = this.recoveryCopies();
    if (!copies || JSON.stringify(copies[copy.cloudCode]) !== JSON.stringify(copy)) return;
    delete copies[copy.cloudCode];
    this.storeRecovery(copies);
  }

  async pullCloud(code, wait = 2500) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), wait);
    this.lastPull = 'error';
    try {
      const res = await fetch(`/api/save/${encodeURIComponent(code)}`, { cache: 'no-store', signal: controller.signal });
      if (res.status === 404) this.lastPull = 'missing';
      if (!res.ok) return null;
      const json = await res.json();
      if (!json || typeof json !== 'object' || !json.v) return null;
      this.lastPull = 'ok';
      return json;
    } catch (e) {
      return null;
    } finally {
      clearTimeout(timeout);
    }
  }

  // On start-up: if the cloud copy is newer (played on another device), use it.
  // 'pulled', 'kept', or 'waiting' when a stand-in couldn't load its player's game yet.
  async syncOnBoot() {
    const code = this.data.cloudCode;
    const remote = await this.pullCloud(code);
    if (this.data.needsPull) {
      // A stand-in for a signed-in player's game: their cloud copy always wins, whatever the clocks say.
      if (remote) {
        this.adopt(remote, code, this.data.playerId, { push: false });
        this.cloudStatus = 'ok';
        return 'pulled';
      }
      if (this.lastPull !== 'missing') return 'waiting';
      // The cloud has no game for this player yet, so this copy becomes it.
      delete this.data.needsPull;
      this.store();
    } else if (remote && (remote.updatedAt || 0) > (this.data.updatedAt || 0) + 1000) {
      this.data = merge(defaultSave(), remote);
      this.data.cloudCode = code;
      this.store();
      this.cloudStatus = 'ok';
      return 'pulled';
    }
    if (!remote || (remote.updatedAt || 0) < (this.data.updatedAt || 0)) this.pushCloud();
    return 'kept';
  }

  async restoreFrom(code) {
    code = String(code || '').trim().toUpperCase().replace(/\s+/g, '-');
    if (!/^[A-Z]+-[A-Z]+-\d{4}$/.test(code)) return { ok: false, msg: 'That code doesn’t look right. It should be like DIAMOND-ROCKET-1234.' };
    const remote = await this.pullCloud(code);
    if (!remote) return { ok: false, msg: 'No save found with that code.' };
    this.data = merge(defaultSave(), remote);
    this.data.cloudCode = code;
    this.persist();
    return { ok: true };
  }

  // Signing in can swap this device to another player's game; device-feel settings stay.
  // push:false keeps a fresh copy local. hydrate:true also makes it a stand-in (needsPull) that is never
  // uploaded until the boot sync loads that player's cloud game or finds they have none.
  adopt(remote, code, playerId, { push = true, hydrate = false } = {}) {
    const keep = this.data.settings;
    this.data = merge(defaultSave(), remote || {});
    if (!remote) for (const k of ['music', 'sfx', 'quality', 'autoJump', 'cameraDistance', 'haptics']) this.data.settings[k] = keep[k];
    this.data.cloudCode = code;
    this.data.playerId = playerId;
    if (hydrate) this.data.needsPull = true;
    else delete this.data.needsPull;
    if (push) return this.persist(true);
    this.store();
  }

  // Worth claiming at first sign-in: real progress made before names and birthdays existed.
  hasProgress() {
    const s = this.data;
    return s.xp > 0 || s.stats.answered > 0 || s.discovery.blocks.length > 0 || Object.keys(s.levels).length > 0;
  }

  resetAll() {
    const { cloudCode, playerId, settings, needsPull, name } = this.data;
    this.data = defaultSave();
    // Progress starts again, but the player keeps their name and sign-in.
    Object.assign(this.data, { cloudCode, playerId, settings, name });
    // Resetting a stand-in doesn't make it this player's game: it still never uploads.
    if (needsPull) this.data.needsPull = true;
    this.persist(true);
  }
}

export const saves = new SaveManager();
